import { handleAxiosError } from '@/api/helpers';
import { FalsePositiveIp } from '@/client/campaigns/types/false-positive-ips.type';
import { IP_PATTERN_REGEX, LoadingStatuses } from '@/common/constants';
import {
  useFalsePositiveIps,
  useUpdateFalsePositiveIpsList,
} from '@/hooks/query/false-positive-ips.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { DateFormats } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { DomainInput } from '@/ui/domain-input';
import { FlexContainer } from '@/ui/styled-ui';
import { TrashIcon } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { isEqual } from 'lodash';
import moment from 'moment';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledList = styled(Card)`
  &.p-card {
    border-radius: var(--medium-border-radius);
    border: 1px solid var(--gray-dark);
    box-shadow: none;

    .p-card-body {
      padding: var(--big-padding);
    }

    .p-card-content {
      display: flex;
      flex-direction: column;
      gap: var(--default-padding);
      padding: 0;
    }

    h2 {
      font-size: var(--heading-small-font-size);
      line-height: var(--heading-small-line-height);
      color: var(--black-main);
    }

    p {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      color: var(--black-main);
    }
  }
  .ips-list {
    margin-top: var(--small-padding);
  }
`;

const StyledIpItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--default-padding-small);
  background-color: var(--beige-main);
  border-radius: var(--xsmall-border-radius);
  margin-bottom: var(--small-padding);
  border: 1px solid var(--gray-dark);

  .ip-info {
    flex: 1;
    width: 100%;

    .ip-address {
      font-weight: 600;
      font-size: var(--medium-font-size);
      color: var(--black-main);
      margin-bottom: var(--xsmall-padding);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .added-info {
      font-size: var(--xsmall-font-size);
    }
  }

  .delete-button {
    color: var(--black-main);
    cursor: pointer;
    padding: var(--xsmall-padding);
    border-radius: var(--small-border-radius);

    &:hover {
      path {
        fill: var(--red-main);
      }
      color: var(--red-main);
    }

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
      cursor: not-allowed;
    }
  }
`;

export const FalsePositiveIpList: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const currentUser = useAppSelector(selectCurrentUser);
  const [error, setError] = useState<string | null>(null);
  const [isChanged, setIsChanged] = useState(false);
  const [newIp, setNewIp] = useState('');
  const [ipsCached, setIpsCached] = useState<FalsePositiveIp[]>([]);
  const [ips, setIps] = useState<FalsePositiveIp[]>([]);

  const {
    falsePositiveIps,
    isLoading: isLoadingIps,
    refetch,
  } = useFalsePositiveIps();
  const { update: updateIps, isLoading: isUpdating } =
    useUpdateFalsePositiveIpsList();

  useEffect(() => {
    setIps(falsePositiveIps?.result || []);
    setIpsCached(falsePositiveIps?.result || []);
  }, [falsePositiveIps?.result]);

  useEffect(() => {
    setIsChanged(!isEqual(ips, ipsCached));
  }, [ips, ipsCached]);

  const handleAddIp = async () => {
    const trimmedIp = newIp.trim();
    if (!IP_PATTERN_REGEX.test(trimmedIp)) {
      setError(t('globalSettings.falsePositiveIpList.invalidIp'));
      return;
    }

    if (
      ips.some((ip) => ip.ipPattern.toLowerCase() === trimmedIp.toLowerCase())
    ) {
      setError(t('globalSettings.falsePositiveIpList.duplicate'));
      return;
    }

    if (trimmedIp) {
      const newIpItem: FalsePositiveIp = {
        ipPattern: trimmedIp,
      };

      setIps([newIpItem, ...ips]);
      setNewIp('');
    }
  };

  const handleDeleteIp = (index: number) => {
    setIps(ips.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      await updateIps({ ips });
      setIsChanged(false);
      toast?.success(
        t('toast.success'),
        t('globalSettings.falsePositiveIpList.saved'),
      );
      await refetch();
    } catch (error) {
      handleAxiosError(error as Error | AxiosError, toast);
    }
  };

  const handleCancel = () => {
    setIps(ipsCached);
  };

  return isLoadingIps ? (
    <FlexContainer direction="column" className="mt-5">
      <ProgressSpinner />
    </FlexContainer>
  ) : (
    <StyledList>
      <FlexContainer justify="space-between" align="center">
        <FlexContainer direction="column" align="flex-start" gap={6}>
          <h2 className="m-0">{t('globalSettings.falsePositiveIpList')}</h2>
          <p className="m-0">
            {t('globalSettings.falsePositiveIpListDescription')}
          </p>
        </FlexContainer>
        <FlexContainer gap={12} justify="flex-end">
          {isChanged && (
            <AppButton
              type="text"
              severity="secondary"
              label={t('globalSettings.cancelChanges')}
              onClick={handleCancel}
              isDisabled={isUpdating}
            />
          )}
          <AppButton
            label={t('globalSettings.saveList')}
            onClick={handleSave}
            isDisabled={!isChanged}
            state={isUpdating ? LoadingStatuses.LOADING : LoadingStatuses.IDLE}
          />
        </FlexContainer>
      </FlexContainer>

      <div>
        <DomainInput
          value={newIp}
          onChange={(value) => {
            setNewIp(value);
            if (error) setError(null);
          }}
          onAdd={handleAddIp}
          placeholder={t('globalSettings.enterIpPlaceholder')}
          message={error ? { text: error, variant: 'error' as const } : null}
          disabled={isUpdating}
        />

        {ips?.length > 0 && (
          <div className="ips-list">
            {ips?.map((ip, index) => (
              <StyledIpItem key={ip.id ?? ip.ipPattern}>
                <div className="ip-info">
                  <div className="ip-address">{ip.ipPattern}</div>
                  <div className="added-info">
                    {t('globalSettings.addedBy', {
                      user: ip.createdBy?.name || currentUser?.name,
                      date: moment(ip.created).format(DateFormats.DATEONLY),
                    })}
                  </div>
                </div>

                <div
                  className={`delete-button ${isUpdating ? 'disabled' : ''}`}
                  onClick={() => !isUpdating && handleDeleteIp(index)}
                >
                  <TrashIcon />
                </div>
              </StyledIpItem>
            ))}
          </div>
        )}
      </div>
    </StyledList>
  );
};
